import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp, ScanSearch, ImagePlus, Camera, Maximize2, Minimize2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { getTerminalCandles, type Candle } from "@/lib/terminal-candles.functions";
import { getAnalystQuota } from "@/lib/analyst.functions";
import climateBear from "@/assets/climate-bear.png";
import climateBull from "@/assets/climate-bull.png";
const climateNeutral =
  "/__l5e/assets-v1/09510e81-de1a-4d5b-923a-d8a8b3b29979/climate-neutral.png";
import { useGoldPrice } from "@/hooks/useGoldPrice";
import { useEconomicCalendar } from "@/hooks/useEconomicCalendar";
import { formatDateLabel, formatTime } from "@/lib/calendar-format";
import { useMarketStats } from "@/components/dashboard/island/hooks/useMarketStats";
import { DeskReading, LiquidityMap, StructureGrid, RangePulse, useIntel } from "@/components/dashboard/terminal/DeskIntel";
import type { DeskIntel } from "@/lib/desk-intel.functions";
import { useWeekendHold, formatReopen } from "@/hooks/useWeekendHold";
import { isWeekendClosed } from "@/components/dashboard/island/lib/marketClock";
import { SeasonalityCard } from "@/components/dashboard/SeasonalityCard";
import { MacroSnapshotCard } from "@/components/dashboard/MacroSnapshotCard";
import { MacroLensPulseCard } from "@/components/dashboard/MacroLensPulseCard";
import { GoldChart } from "@/components/dashboard/terminal/GoldChart";
import { OrionMind, Macro, StructureLensUpload } from "@/components/dashboard/terminal/CommandWidgets";

export const Route = createFileRoute("/_authenticated/admin/wip/command-center")({
  head: () => ({
    meta: [
      { title: "Orion Command Center | XAU Pro Academy" },
      { name: "description", content: "Orion daily terminal: live gold price, market climate, proprietary chart, macro shield and the decision desk." },
      { property: "og:title", content: "Orion Command Center | XAU Pro Academy" },
      { property: "og:description", content: "Live gold terminal with market climate, macro shield and qualified decisions." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: CommandCenter,
});

import { resolveClimate, type Climate } from "@/components/dashboard/command/marketClimate";
type TF = "M5" | "M15" | "H1" | "H4" | "D1";
const TF_MINUTE: Record<TF, string> = { M5: "5 minute", M15: "15 minute", H1: "1 hour", H4: "4 hour", D1: "Daily" };
const TF_STEP: Record<TF, number> = { M5: 300, M15: 900, H1: 3600, H4: 14400, D1: 86400 };

const CLIMATE = {
  bull: { label: "Bullish Expansion", tone: "var(--bias-bull)", note: "Constructive tape. Buyers defending higher lows." },
  bear: { label: "Bearish Pressure", tone: "var(--bias-bear)", note: "Distribution phase. Sellers in control of the session." },
  neutral: { label: "Balanced Range", tone: "var(--gold)", note: "Rotational conditions. Awaiting directional commitment." },
} as const;

function CommandCenter() {
  const fetchCandles = useServerFn(getTerminalCandles);
  const { price: liveSpotPrice } = useGoldPrice(15_000);
  const [tf, setTf] = useState<TF>("M15");
  const { data: intel } = useIntel();
  const [overlay, setOverlay] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setExpanded(false); };
    window.addEventListener("keydown", onKey);
    const b = document.body;
    const prev = b.style.overflow, prevPad = b.style.paddingRight;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    b.style.overflow = "hidden";
    if (gap > 0) b.style.paddingRight = `${gap}px`;
    return () => { window.removeEventListener("keydown", onKey); b.style.overflow = prev; b.style.paddingRight = prevPad; };
  }, [expanded]);
  const [focusPulse, setFocusPulse] = useState(false);
  const chartRef = useRef<HTMLElement>(null);
  const showOnChart = () => {
    setOverlay((v) => !v);
    chartRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setFocusPulse(true);
    window.setTimeout(() => setFocusPulse(false), 2200);
  };
  const { data } = useQuery({
    queryKey: ["terminal-candles", tf],
    queryFn: () => fetchCandles({ data: { tf } }),
    refetchInterval: 60_000,
  });
  const candles = data?.candles ?? [];
  const lastClosedCandle = candles.at(-1)?.c ?? null;
  const last = liveSpotPrice ?? lastClosedCandle;
  const weekendHold = useWeekendHold();
  const chartCandles = useMemo(() => {
    // During weekend hold the market is closed: no synthetic live candle,
    // otherwise Friday High/Low collapse to the stale last quote.
    if (weekendHold.closed) return candles;
    if (last == null || candles.length === 0) return candles;
    const step = TF_STEP[tf];
    const off = tf === "D1" ? 7200 : 0;
    const currentBucket = Math.floor((Date.now() / 1000 + off) / step) * step - off;
    const latest = candles.at(-1);
    if (!latest) return candles;
    const liveCandle: Candle = latest.t === currentBucket
      ? { ...latest, h: Math.max(latest.h, last), l: Math.min(latest.l, last), c: last }
      : { t: currentBucket, o: latest.c, h: Math.max(latest.c, last), l: Math.min(latest.c, last), c: last };
    return latest.t === currentBucket
      ? [...candles.slice(0, -1), liveCandle]
      : [...candles.slice(-119), liveCandle];
  }, [candles, last, tf, weekendHold.closed]);
  // Daily change is anchored on the canonical server previousClose (same
  // reference as the live dashboard), not the session open, so weekend and
  // session gaps are included.
  const stats = useMarketStats();
  const prevClose = stats.snapshot?.previousClose ?? null;
  const delta = last != null && prevClose ? last - prevClose : null;
  const pct = delta != null && prevClose ? (delta / prevClose) * 100 : 0;
  // Shared hysteresis resolver, identical to the Trading Room.
  const climate: Climate = resolveClimate(delta != null ? pct : null);
  // Same structure source as the Structure Matrix and Trading Room: when D1
  // and H4 disagree, the banner reports an intraday move, not an expansion.
  const d1 = intel?.structure?.find((r) => r.tf === "D1")?.bias;
  const h4 = intel?.structure?.find((r) => r.tf === "H4")?.bias;
  const htfMixed = d1 != null && h4 != null && d1 !== h4;
  const c =
    climate === "bull" && htfMixed
      ? { label: "Intraday Advance", tone: "var(--bias-bull)", note: "Buyers in control of the session. Higher timeframes mixed." }
      : climate === "bear" && htfMixed
        ? { label: "Intraday Decline", tone: "var(--bias-bear)", note: "Sellers in control of the session. Higher timeframes mixed." }
        : CLIMATE[climate];

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ ["--tone" as string]: c.tone }}>
      {/* Atmosphere */}
      <div aria-hidden className="pointer-events-none absolute inset-0 transition-all duration-1000"
        style={{ background: "radial-gradient(ellipse 70% 45% at 50% -5%, color-mix(in oklab, var(--tone) 22%, transparent), transparent 70%)" }} />

      <div className="relative container mx-auto max-w-7xl px-4 py-6 space-y-5">

        {/* [1] Climate strip */}
        <section className="rounded-lg border bg-card/40 backdrop-blur-xl p-5 transition-colors duration-1000"
          style={{ borderColor: "color-mix(in oklab, var(--tone) 40%, var(--border))" }}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.24em]" style={{ color: "var(--tone)" }}>Market Climate</div>
              <h1 className="mt-1 font-display text-3xl">{c.label}</h1>
              <p className="mt-1 text-sm text-muted-foreground">{c.note}</p>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">XAUUSD</div>
              <div className="font-mono text-4xl tabular-nums">{last?.toFixed(2) ?? "..."}</div>
              <div className="font-mono text-sm tabular-nums" style={{ color: "var(--tone)" }}>
                {delta != null ? `${delta >= 0 ? "+" : ""}${delta.toFixed(2)} (${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%) ${weekendHold.closed ? "at Friday's close" : "today"}` : "..."}
              </div>
            </div>
          </div>
        </section>

        {/* [2] Mind + Chart */}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          <OrionMind climate={climate} />
          {expanded && (
            <div
              aria-hidden
              onClick={() => setExpanded(false)}
              className="fixed inset-0 z-40 bg-background/85 backdrop-blur-xl animate-chart-fade"
            />
          )}
          <section
            ref={chartRef}
            className={`min-w-0 lg:col-span-8 xl:col-span-6 rounded-lg border bg-card/40 backdrop-blur-xl p-4 transition-[border-color,box-shadow] duration-700 ${focusPulse ? "border-gold shadow-[0_0_0_3px_color-mix(in_oklab,var(--gold)_28%,transparent)]" : "border-gold/40"} ${expanded ? "fixed left-1/2 top-1/2 z-50 w-[94vw] max-w-[1400px] -translate-x-1/2 -translate-y-1/2 bg-card shadow-2xl animate-chart-fade" : ""}`}
          >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">XAUUSD Chart</div>
                <div className="text-sm text-muted-foreground">{TF_MINUTE[tf]}</div>
              </div>
              <div className="flex flex-wrap gap-1">
                {(["M5", "M15", "H1", "H4", "D1"] as const).map((k) => (
                  <button key={k} onClick={() => setTf(k)}
                    className={`rounded px-2.5 py-1 font-mono text-[11px] border ${tf === k ? "border-gold text-gold bg-gold-muted" : "border-border/60 text-muted-foreground"}`}>{k}</button>
                ))}
                <button
                  onClick={() => setOverlay((v) => !v)}
                  aria-pressed={overlay}
                  className={`rounded px-2.5 py-1 font-mono text-[11px] border ${overlay ? "border-gold text-gold bg-gold-muted" : "border-border/60 text-muted-foreground"}`}
                >
                  Desk layer
                </button>
                <button
                  onClick={() => setExpanded((v) => !v)}
                  aria-label={expanded ? "Close expanded chart" : "Expand chart"}
                  title={expanded ? "Close (Esc)" : "Expand chart"}
                  className="rounded border border-border/60 px-2 py-1 text-muted-foreground transition-colors hover:border-gold/60 hover:text-gold"
                >
                  {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
            <div className="relative">
              {overlay && (
                <div className="pointer-events-none absolute bottom-2 left-2 z-10 flex items-center gap-2.5 rounded-md border border-gold/30 bg-background/70 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] backdrop-blur-sm animate-chart-fade">
                  <span className="flex items-center gap-1.5 text-gold">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-gold" /> Desk overlay
                  </span>
                  <span className="hidden text-gold/80 sm:inline">Sweeps</span>
                  <span className="hidden sm:inline" style={{ color: "var(--bias-bull)" }}>Buy study</span>
                  <span className="hidden sm:inline" style={{ color: "var(--bias-bear)" }}>Sell study</span>
                </div>
              )}
              <GoldChart candles={chartCandles} dayOpen={data?.dayOpen ?? null} intel={overlay ? intel ?? null : null} tall={expanded} />
            </div>
          </section>
          <StructureLensUpload />
        </div>

        {/* [2b] Live MacroLens confluence */}
        <MacroLensPulseCard />

        {/* [3] Featured tactical reading */}
        <DeskReading overlayOn={overlay} onShowOnChart={showOnChart} />

        {/* [4] Liquidity map + structure matrix + range and sessions */}
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          <LiquidityMap />
          <StructureGrid />
          <RangePulse />
        </div>

        {/* [4] Why + Levels + Macro */}
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          <WhyCard climate={climate} />
          <Levels candles={chartCandles} dayOpen={data?.dayOpen ?? null} />
          <Macro />
        </div>
        <div className="space-y-5 border-t border-border/60 pt-6">
          <h2 className="font-mono text-xs uppercase text-gold">Fundamentals & historical context</h2>
          <SeasonalityCard />
          <MacroSnapshotCard />
        </div>
      </div>
    </div>
  );
}

function WhyCard({ climate }: { climate: Climate }) {
  const dir = climate === "bull" ? "Bullish" : climate === "bear" ? "Bearish" : "Neutral";
  const img = climate === "bull" ? climateBull : climate === "bear" ? climateBear : climateNeutral;
  const { data: intel } = useIntel();
  const { events } = useEconomicCalendar();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // HTF structure: D1 and H4 bias from the desk intel engine.
  const d1 = intel?.structure.find((s) => s.tf === "D1")?.bias;
  const h4 = intel?.structure.find((s) => s.tf === "H4")?.bias;
  const htfBias = d1 && d1 === h4 && d1 !== "flat" ? d1 : null;
  const htf = !intel ? "Loading" : htfBias === "bull" ? "D1 + H4 bullish" : htfBias === "bear" ? "D1 + H4 bearish" : "Mixed";

  // Liquidity: which mapped pools have actually been taken today.
  const liq = intel?.liquidity;
  const swept = liq
    ? [
        liq.prevDayHighSwept && "PDH",
        liq.prevDayLowSwept && "PDL",
        liq.asianHighSwept && "Asian high",
        liq.asianLowSwept && "Asian low",
      ].filter(Boolean) as string[]
    : [];
  const liquidity = !liq ? "Loading" : swept.length ? `${swept.slice(0, 2).join(", ")} swept` : "No sweep yet";

  // Session: UTC clock against the desk execution windows.
  const d = new Date(now);
  const mins = d.getUTCHours() * 60 + d.getUTCMinutes();
  const weekend = isWeekendClosed(d);
  const session = weekend ? "Market closed"
    : mins >= 420 && mins < 600 ? "London (active)"
    : mins >= 600 && mins < 720 ? "Midday freeze"
    : mins >= 720 && mins < 1080 ? "New York (active)"
    : mins < 420 ? "Asia (low liquidity)" : "Off hours";
  const sessionActive = session.includes("active");

  // Macro: next calendar event within two hours.
  const next = events?.find((e) => { const t = Date.parse(e.date); return Number.isFinite(t) && t > now; });
  const untilMin = next ? Math.round((Date.parse(next.date) - now) / 60_000) : null;
  const macroRisk = untilMin != null && untilMin <= 120;
  const macro = !events ? "Loading" : macroRisk ? `Event in ${untilMin}m` : "Clear";

  const directionalMatch = htfBias != null && climate !== "neutral" && (htfBias === "bull") === (climate === "bull");
  // Weekend: session and macro do not apply to a closed market, so the
  // confluence is scored on the weekly close factors only.
  const score = weekend
    ? [directionalMatch, swept.length > 0].filter(Boolean).length
    : [directionalMatch, swept.length > 0, sessionActive, !!events && !macroRisk].filter(Boolean).length;

  const rows: [string, string][] = [
    ["HTF Structure", htf],
    ["Liquidity", liquidity],
    ["Session", session],
    ["Macro", macro],
    ["Confluence", weekend ? `${score} / 2 weekly close factors` : `${score} / 4 factors`],
  ];
  return (
    <section className="relative overflow-hidden rounded-lg border bg-card/40 backdrop-blur-xl p-4 transition-colors duration-1000"
      style={{ borderColor: "color-mix(in oklab, var(--tone) 35%, var(--border))" }}>
      {img && (
        <img src={img} alt={`Gold market climate: ${climate}`} loading="lazy"
          width={climate === "neutral" ? 995 : 816} height={climate === "neutral" ? 416 : 816}
          className={`pointer-events-none absolute bottom-0 right-0 opacity-70 transition-opacity duration-1000 ${climate === "neutral" ? "w-56" : "w-40"}`}
          style={{ maskImage: `linear-gradient(to right, transparent 0%, black ${climate === "neutral" ? 22 : 45}%)`, WebkitMaskImage: `linear-gradient(to right, transparent 0%, black ${climate === "neutral" ? 22 : 45}%)` }} />
      )}
      <div className="relative">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em]" style={{ color: "var(--tone)" }}>
          {weekend ? `Weekly closing bias: ${dir}` : `Why Orion is ${dir} today`}
        </div>
        <ul className="mt-3 space-y-2.5">
          {rows.map(([k, v]) => (
            <li key={k} className="grid grid-cols-[6.5rem_1fr] items-center gap-2 text-sm">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "var(--tone)" }} />
                {k}
              </span>
              <span className="font-mono text-xs tabular-nums">{v}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Levels({ candles, dayOpen }: { candles: Candle[]; dayOpen: number | null }) {
  const { closed: weekendClosed } = useWeekendHold();
  // Today only (UTC day of the latest bar), so the range never mixes in the prior session.
  // During weekend hold the latest bar is Friday's, so the labels say so.
  const lastT = candles.at(-1)?.t ?? 0;
  const dayStart = Math.floor(lastT / 86400) * 86400;
  const today = candles.filter((k) => k.t >= dayStart);
  const hi = today.length ? Math.max(...today.map((k) => k.h)) : null;
  const lo = today.length ? Math.min(...today.map((k) => k.l)) : null;
  const last = candles.at(-1)?.c ?? null;
  const pos = hi != null && lo != null && last != null ? ((last - lo) / (hi - lo || 1)) * 100 : 50;
  const rows: [string, number | null][] = weekendClosed
    ? [["Friday High", hi], ["Friday Open", dayOpen], ["Friday Low", lo]]
    : [["Today High", hi], ["Day Open", dayOpen], ["Today Low", lo]];
  return (
    <section className="rounded-lg border border-border/60 bg-card/40 p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Daily Levels</div>
      <div className="mt-3 space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between text-sm"><span className="text-muted-foreground">{k}</span><span className="font-mono tabular-nums">{v?.toFixed(2) ?? "..."}</span></div>
        ))}
      </div>
      <div className="mt-4">
        <div className="flex justify-between font-mono text-[9px] uppercase text-muted-foreground"><span>Discount</span><span>Premium</span></div>
        <div className="relative mt-1 h-1.5 rounded-full bg-border/60">
          <div className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background" style={{ left: `${pos}%`, background: "var(--tone)" }} />
        </div>
      </div>
    </section>
  );
}
